import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus, Search, SlidersHorizontal, Upload, User, X } from 'lucide-react';
import { AppLayout } from '@/components/ui/AppLayout';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Banner } from '@/components/ui/Banner';
import { Drawer } from '@/components/ui/Drawer';
import { StatusBadge, PotentialBadge, Badge } from '@/components/ui/StatusBadge';
import { displayName } from '@shared/prospect';
import { useRealtimeEvent, REALTIME_EVENTS } from '@/hooks/useRealtimeEvent';
import { CreateProspectModal } from '@/components/CreateProspectModal';
import { ImportProspectsModal } from '@/components/ImportProspectsModal';
import { formatRelative } from '@/components/TimelineSection';
import {
  prospectsApi,
  type ProspectFilters,
  type ProspectListItem,
} from '@/services/prospects.api';
import { configApi, type ContractType } from '@/services/config.api';
import { usersApi, type TeamMember } from '@/services/users.api';
import { STAGE_LABELS, type OpportunityStage } from '@/services/opportunities.api';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Filtres - helpers URL sync
// ---------------------------------------------------------------------------

// Clés de filtre persistées dans l'URL. Toutes optionnelles - si vide, on
// retire le paramètre pour garder l'URL propre. Aligné 1-pour-1 avec le
// backend `listProspectsQuery`.
type FilterKey =
  | 'type'
  | 'status'
  | 'potential'
  | 'assigned_user_id'
  | 'search'
  | 'naf_code'
  | 'postal_code'
  | 'department'
  | 'near_location'
  | 'radius_km'
  | 'has_opportunities'
  | 'has_signed_opp'
  | 'active_opp_stages'
  | 'subscribed_contract_type_ids'
  | 'min_signed_revenue'
  | 'min_employee_count'
  | 'max_employee_count'
  | 'min_revenue'
  | 'max_revenue'
  | 'company_created_after'
  | 'company_created_before'
  | 'collective_agreement'
  | 'created_after'
  | 'created_before'
  | 'updated_after'
  | 'updated_before'
  | 'last_activity_after'
  | 'last_activity_before'
  | 'no_activity_since'
  | 'include_deleted';

type FilterValues = Partial<Record<FilterKey, string>>;

const FILTER_KEYS: readonly FilterKey[] = [
  'type',
  'status',
  'potential',
  'assigned_user_id',
  'search',
  'naf_code',
  'postal_code',
  'department',
  'near_location',
  'radius_km',
  'has_opportunities',
  'has_signed_opp',
  'active_opp_stages',
  'subscribed_contract_type_ids',
  'min_signed_revenue',
  'min_employee_count',
  'max_employee_count',
  'min_revenue',
  'max_revenue',
  'company_created_after',
  'company_created_before',
  'collective_agreement',
  'created_after',
  'created_before',
  'updated_after',
  'updated_before',
  'last_activity_after',
  'last_activity_before',
  'no_activity_since',
  'include_deleted',
] as const;

// Convertit un `input type="date"` (YYYY-MM-DD) en ISO datetime UTC à
// minuit - format attendu par le backend (z.string().datetime()).
function dateToIso(date: string): string {
  return date ? new Date(`${date}T00:00:00Z`).toISOString() : '';
}
function isoToDate(iso: string | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

// Transforme les valeurs string de l'URL en ProspectFilters typé pour l'API.
function toApiFilters(v: FilterValues): ProspectFilters {
  const f: ProspectFilters = {};
  if (v.type) f.type = v.type as ProspectFilters['type'];
  if (v.status) f.status = v.status as ProspectListItem['status'];
  if (v.potential) f.potential = v.potential as ProspectListItem['potential'];
  if (v.assigned_user_id) f.assigned_user_id = v.assigned_user_id;
  if (v.search) f.search = v.search;
  if (v.naf_code) f.naf_code = v.naf_code;
  if (v.postal_code) f.postal_code = v.postal_code;
  if (v.department) f.department = v.department;
  if (v.near_location) f.near_location = v.near_location;
  if (v.radius_km) f.radius_km = Number(v.radius_km);
  if (v.has_opportunities === 'true') f.has_opportunities = true;
  if (v.has_opportunities === 'false') f.has_opportunities = false;
  if (v.has_signed_opp === 'true') f.has_signed_opp = true;
  if (v.active_opp_stages) f.active_opp_stages = v.active_opp_stages;
  if (v.subscribed_contract_type_ids)
    f.subscribed_contract_type_ids = v.subscribed_contract_type_ids;
  if (v.min_signed_revenue) f.min_signed_revenue = Number(v.min_signed_revenue);
  if (v.min_employee_count) f.min_employee_count = Number(v.min_employee_count);
  if (v.max_employee_count) f.max_employee_count = Number(v.max_employee_count);
  if (v.min_revenue) f.min_revenue = Number(v.min_revenue);
  if (v.max_revenue) f.max_revenue = Number(v.max_revenue);
  if (v.company_created_after) f.company_created_after = v.company_created_after;
  if (v.company_created_before) f.company_created_before = v.company_created_before;
  if (v.collective_agreement) f.collective_agreement = v.collective_agreement;
  if (v.created_after) f.created_after = v.created_after;
  if (v.created_before) f.created_before = v.created_before;
  if (v.updated_after) f.updated_after = v.updated_after;
  if (v.updated_before) f.updated_before = v.updated_before;
  if (v.last_activity_after) f.last_activity_after = v.last_activity_after;
  if (v.last_activity_before) f.last_activity_before = v.last_activity_before;
  if (v.no_activity_since) f.no_activity_since = Number(v.no_activity_since);
  if (v.include_deleted === 'true') f.include_deleted = true;
  return f;
}

// ===========================================================================
// Page
// ===========================================================================

export function ProspectsPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const currentUser = useAuthStore((s) => s.user);
  const canImport = currentUser?.role === 'admin' || currentUser?.role === 'director';
  const isDirection =
    currentUser?.role === 'admin' ||
    currentUser?.role === 'director' ||
    currentUser?.role === 'executive';

  const [searchParams, setSearchParams] = useSearchParams();

  // Hydrate initial filters from URL.
  const [filters, setFilters] = useState<FilterValues>(() => {
    const init: FilterValues = {};
    for (const k of FILTER_KEYS) {
      const val = searchParams.get(k);
      if (val) init[k] = val;
    }
    return init;
  });

  const [items, setItems] = useState<ProspectListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(() => searchParams.get('new') === '1');
  const [importOpen, setImportOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Réagit à l'apparition de `?new=1` après le mount (cas : on est déjà sur
  // /prospects et la CommandPalette navigue vers /prospects?new=1 — le query
  // param change mais le composant ne remount pas, donc l'initializer lazy
  // du useState ci-dessus ne se réévalue pas).
  useEffect(() => {
    if (searchParams.get('new') === '1' && !modalOpen) {
      setModalOpen(true);
    }
  }, [searchParams, modalOpen]);

  // État local de la barre de recherche toolbar. Débounce 250 ms vers
  // `filters.search` pour ne pas spammer le backend à chaque frappe.
  // Synchro bidirectionnelle avec le champ Recherche du drawer : les deux
  // éditent le même filtre, modifier l'un met à jour l'autre.
  const [localSearch, setLocalSearch] = useState(filters.search ?? '');

  // localSearch → filters.search (debounced)
  useEffect(() => {
    const next = localSearch.trim() || null;
    const current = filters.search ?? null;
    if (next === current) return;
    const timer = setTimeout(() => setFilter('search', next), 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localSearch]);

  // filters.search → localSearch (ex. édité depuis le drawer ou clear depuis pills)
  useEffect(() => {
    setLocalSearch((prev) => (prev === (filters.search ?? '') ? prev : (filters.search ?? '')));
  }, [filters.search]);

  // Référentiels chargés une fois pour peupler les dropdowns du drawer.
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      configApi.listContractTypes(token),
      // Direction → tous les users ; sinon → membres de l'équipe.
      isDirection ? usersApi.list(token, { activeOnly: true }) : usersApi.teamMembers(token),
    ]).then(([cts, users]) => {
      if (cancelled) return;
      setContractTypes(cts.filter((c) => c.isActive));
      setTeamMembers(users);
    });
    return () => {
      cancelled = true;
    };
  }, [token, isDirection]);

  // URL sync : push les filtres non vides dans l'URL en mode replace (pas
  // de spam d'historique). Préserve `new=1` (modal de création).
  useEffect(() => {
    const next = new URLSearchParams();
    for (const k of FILTER_KEYS) {
      const v = filters[k];
      if (v) next.set(k, v);
    }
    if (searchParams.get('new')) next.set('new', '1');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const activeFilterCount = useMemo(() => FILTER_KEYS.filter((k) => filters[k]).length, [filters]);

  const setFilter = useCallback((key: FilterKey, value: string | null) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value === null || value === '') delete next[key];
      else next[key] = value;
      // Couple `near_location` ↔ `radius_km` : indissociables côté backend
      // (refine Zod). Si l'un est vidé, on vide aussi l'autre pour éviter
      // un 422 silencieux.
      if ((key === 'near_location' || key === 'radius_km') && (value === null || value === '')) {
        delete next.near_location;
        delete next.radius_km;
      }
      return next;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);

  // Compteurs pour la summary line (calculés à partir des pages chargées -
  // approximation acceptable en V1).
  const counts = useMemo(() => {
    const byStatus = { cold: 0, warm: 0, client: 0 };
    for (const p of items) byStatus[p.status] += 1;
    return byStatus;
  }, [items]);

  const load = useCallback(
    async (cursor?: string): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const res = await prospectsApi.list(token, {
          cursor,
          limit: 20,
          ...toApiFilters(filters),
        });
        setItems((prev) => (cursor ? [...prev, ...res.data] : res.data));
        setNextCursor(res.nextCursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement');
      } finally {
        setLoading(false);
      }
    },
    [token, filters],
  );

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Realtime : refresh liste à chaque create/update/delete de prospect
  // (par soi ou par un collègue de l'équipe). Filtre et pagination sont
  // réappliqués au serveur → on garde la cohérence sans logique locale.
  useRealtimeEvent(
    REALTIME_EVENTS.PROSPECT_UPDATED,
    useCallback(() => {
      void load();
    }, [load]),
  );

  const columns: Column<ProspectListItem>[] = [
    {
      key: 'displayName',
      header: 'Nom',
      render: (p) => (
        <Link
          to={`/prospects/${p.id}`}
          onClick={(e) => e.stopPropagation()}
          className="group block"
          // Tooltip natif au hover : utile quand le nom est tronqué
          title={displayName(p)}
        >
          <div className="flex max-w-[280px] items-center gap-2">
            {p.type === 'individual' ? (
              <User className="h-3.5 w-3.5 shrink-0 text-text-muted" />
            ) : (
              <Building2 className="h-3.5 w-3.5 shrink-0 text-text-muted" />
            )}
            {/* min-w-0 indispensable dans un flex pour que truncate fonctionne
                sur les enfants — sinon le contenu déborde son container. */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate font-medium text-brand group-hover:text-accent">
                  {displayName(p)}
                </span>
                {p.isPPE && (
                  <Badge tone="warning" className="shrink-0">
                    PPE
                  </Badge>
                )}
              </div>
              <div className="truncate text-xs text-text-muted">{p.addressCity ?? '-'}</div>
            </div>
          </div>
        </Link>
      ),
    },
    {
      key: 'identifier',
      header: 'SIRET / Contact',
      render: (p) =>
        p.type === 'company' ? (
          <span className="font-mono text-xs text-text-muted">{p.siret ?? '-'}</span>
        ) : (
          <span className="text-xs text-text-muted">
            {p.personalEmail ?? p.personalPhone ?? '-'}
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Statut',
      render: (p) => <StatusBadge status={p.status} />,
    },
    {
      key: 'potential',
      header: 'Potentiel',
      render: (p) => <PotentialBadge potential={p.potential} />,
    },
    {
      key: 'assignedUser',
      header: 'Commercial',
      render: (p) =>
        p.assignedUser ? (
          <span className="text-text-primary">
            {p.assignedUser.firstName} {p.assignedUser.lastName}
          </span>
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: 'lastActivityAt',
      header: 'Dernière activité',
      render: (p) => (
        <span
          className="text-xs text-text-muted"
          title={p.lastActivityAt ? new Date(p.lastActivityAt).toLocaleString('fr-FR') : undefined}
        >
          {p.lastActivityAt ? formatRelative(p.lastActivityAt) : '-'}
        </span>
      ),
    },
    {
      key: 'opportunities',
      header: 'Opps',
      align: 'right',
      render: (p) => <span className="text-text-primary">{p._count.opportunities}</span>,
    },
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Prospects"
        subtitle="Pipeline commercial - fiches entreprises qualifiées ou à qualifier."
        actions={
          <div className="flex gap-2">
            {canImport && (
              <Button
                variant="secondary"
                leadingIcon={<Upload className="h-4 w-4" />}
                onClick={() => setImportOpen(true)}
              >
                Importer
              </Button>
            )}
            <Button
              variant="primary"
              leadingIcon={<Plus className="h-4 w-4" />}
              onClick={() => setModalOpen(true)}
            >
              Nouveau prospect
            </Button>
          </div>
        }
      />

      {/* Toolbar : search à gauche + bouton Filtres à droite (pattern Stripe
          Dashboard 2024). La search bar édite `filters.search` avec debounce
          250 ms pour un feedback UI live sans spammer le backend. */}
      <div className="mb-4 flex items-center gap-3">
        <SearchBar
          value={localSearch}
          onChange={setLocalSearch}
          onClear={() => setLocalSearch('')}
        />
        <Button
          variant="secondary"
          leadingIcon={<SlidersHorizontal className="h-4 w-4" />}
          onClick={() => setDrawerOpen(true)}
          className="ml-auto"
        >
          Filtres{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </Button>
      </div>

      <ActiveFilterPills
        filters={filters}
        contractTypes={contractTypes}
        teamMembers={teamMembers}
        onRemove={(k) => setFilter(k, null)}
        onClearAll={clearAll}
      />

      {error && (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      )}

      {!loading && items.length > 0 && (
        <p className="mb-3 text-xs text-text-muted">
          {items.length} prospect{items.length > 1 ? 's' : ''} affiché
          {items.length > 1 ? 's' : ''}
          {counts.cold > 0 && ` · ${counts.cold} froid${counts.cold > 1 ? 's' : ''}`}
          {counts.warm > 0 && ` · ${counts.warm} chaud${counts.warm > 1 ? 's' : ''}`}
          {counts.client > 0 && ` · ${counts.client} client${counts.client > 1 ? 's' : ''}`}
          {nextCursor && ' · plus de résultats disponibles'}
        </p>
      )}

      <DataTable
        columns={columns}
        data={items}
        keyExtractor={(p) => p.id}
        loading={loading && items.length === 0}
        onRowClick={(p) => navigate(`/prospects/${p.id}`)}
        empty={{
          title: 'Aucun prospect',
          description:
            activeFilterCount > 0
              ? 'Aucun prospect ne correspond aux filtres actifs. Essayez d\u2019en retirer.'
              : 'Commencez par créer votre première fiche entreprise.',
          action:
            activeFilterCount > 0 ? (
              <Button variant="secondary" onClick={clearAll}>
                Effacer les filtres
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setModalOpen(true)}>
                Créer un prospect
              </Button>
            ),
        }}
      />

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            onClick={() => void load(nextCursor)}
            disabled={loading}
            leadingIcon={<Building2 className="h-3.5 w-3.5" />}
          >
            {loading ? 'Chargement…' : 'Charger plus'}
          </Button>
        </div>
      )}

      {modalOpen && (
        <CreateProspectModal
          onClose={() => {
            setModalOpen(false);
            if (searchParams.get('new')) {
              const next = new URLSearchParams(searchParams);
              next.delete('new');
              setSearchParams(next, { replace: true });
            }
          }}
          onCreated={(p) => {
            setModalOpen(false);
            setItems((prev) => [p as ProspectListItem, ...prev]);
            toast({
              tone: 'success',
              title: 'Prospect créé',
              message: `« ${displayName(p)} » a été ajouté à votre pipeline.`,
            });
            if (searchParams.get('new')) {
              const next = new URLSearchParams(searchParams);
              next.delete('new');
              setSearchParams(next, { replace: true });
            }
          }}
        />
      )}
      {importOpen && (
        <ImportProspectsModal
          onClose={() => setImportOpen(false)}
          onImported={() => {
            void load();
          }}
        />
      )}

      <FiltersDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeCount={activeFilterCount}
        filters={filters}
        setFilter={setFilter}
        onClearAll={clearAll}
        contractTypes={contractTypes}
        teamMembers={teamMembers}
        isDirection={isDirection}
      />
    </AppLayout>
  );
}

// ===========================================================================
// FiltersDrawer
// ===========================================================================

interface FiltersDrawerProps {
  open: boolean;
  onClose: () => void;
  activeCount: number;
  filters: FilterValues;
  setFilter: (key: FilterKey, value: string | null) => void;
  onClearAll: () => void;
  contractTypes: ContractType[];
  teamMembers: TeamMember[];
  isDirection: boolean;
}

// Ordonne pipeline stages pour les toggles multi-stages. Aligné avec le
// pipeline Kanban (ordre logique non alphabétique).
const ALL_STAGES: readonly OpportunityStage[] = [
  'suspect',
  'contacted',
  'meeting_booked',
  'meeting_done',
  'awaiting_client_docs',
  'awaiting_quote_supplier',
  'quote_sent',
  'won',
  'lost',
  'no_follow_up',
];

function FiltersDrawer(p: FiltersDrawerProps) {
  const activeStages = p.filters.active_opp_stages
    ? p.filters.active_opp_stages.split(',').filter(Boolean)
    : [];
  const subscribedTypes = p.filters.subscribed_contract_type_ids
    ? p.filters.subscribed_contract_type_ids.split(',').filter(Boolean)
    : [];

  // Types de contrats à afficher dans les chips selon le filtre `type` :
  // - type=individual → uniquement applicableToIndividual
  // - type=company → uniquement applicableToCompany
  // - aucun type → tous
  const prospectType = p.filters.type;
  const filteredContractTypes = useMemo(() => {
    if (prospectType === 'individual')
      return p.contractTypes.filter((ct) => ct.applicableToIndividual);
    if (prospectType === 'company') return p.contractTypes.filter((ct) => ct.applicableToCompany);
    return p.contractTypes;
  }, [p.contractTypes, prospectType]);

  // Nettoyage : si l'utilisateur change le filtre type et que des contrats
  // souscrits ne sont plus applicables, on retire les IDs orphelins pour
  // éviter un résultat vide silencieux. Guard sur "next === current" pour
  // éviter une boucle de re-render.
  useEffect(() => {
    if (subscribedTypes.length === 0) return;
    const allowed = new Set(filteredContractTypes.map((ct) => ct.id));
    const next = subscribedTypes.filter((id) => allowed.has(id));
    if (next.length !== subscribedTypes.length) {
      p.setFilter('subscribed_contract_type_ids', next.length > 0 ? next.join(',') : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredContractTypes]);

  function toggleActiveStage(stage: string) {
    const next = activeStages.includes(stage)
      ? activeStages.filter((s) => s !== stage)
      : [...activeStages, stage];
    p.setFilter('active_opp_stages', next.length > 0 ? next.join(',') : null);
  }

  function toggleContractType(id: string) {
    const next = subscribedTypes.includes(id)
      ? subscribedTypes.filter((s) => s !== id)
      : [...subscribedTypes, id];
    p.setFilter('subscribed_contract_type_ids', next.length > 0 ? next.join(',') : null);
  }

  return (
    <Drawer
      open={p.open}
      onClose={p.onClose}
      title="Filtres"
      width="md"
      footer={
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-text-muted">
            {p.activeCount === 0
              ? 'Aucun filtre actif'
              : `${p.activeCount} filtre${p.activeCount > 1 ? 's' : ''} actif${p.activeCount > 1 ? 's' : ''}`}
          </span>
          <Button variant="secondary" onClick={p.onClearAll} disabled={p.activeCount === 0}>
            Tout effacer
          </Button>
        </div>
      }
    >
      <div>
        {/* ===== 0. Type de prospect ===== */}
        <FilterSection title="Type">
          <FilterLabel>Type de prospect</FilterLabel>
          <Select
            className="mt-1.5"
            value={p.filters.type ?? ''}
            onChange={(e) => p.setFilter('type', e.target.value || null)}
          >
            <option value="">Tous</option>
            <option value="company">Entreprises</option>
            <option value="individual">Particuliers</option>
          </Select>
        </FilterSection>

        {/* ===== 1. Qualification ===== */}
        <FilterSection title="Qualification">
          <FilterLabel>Statut</FilterLabel>
          <Select
            className="mt-1.5"
            value={p.filters.status ?? ''}
            onChange={(e) => p.setFilter('status', e.target.value || null)}
          >
            <option value="">Tous</option>
            <option value="cold">Froid</option>
            <option value="warm">Chaud</option>
            <option value="client">Client</option>
          </Select>
          <FilterLabel>Potentiel (auto)</FilterLabel>
          <Select
            className="mt-1.5"
            value={p.filters.potential ?? ''}
            onChange={(e) => p.setFilter('potential', e.target.value || null)}
          >
            <option value="">Tous</option>
            <option value="low">Faible</option>
            <option value="medium">Moyen</option>
            <option value="high">Fort</option>
            <option value="very_high">Très fort</option>
          </Select>
          <FilterLabel>Commercial assigné</FilterLabel>
          <Select
            className="mt-1.5"
            value={p.filters.assigned_user_id ?? ''}
            onChange={(e) => p.setFilter('assigned_user_id', e.target.value || null)}
          >
            <option value="">Tous</option>
            {p.teamMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </Select>
        </FilterSection>

        {/* ===== 2. Recherche / géo ===== */}
        <FilterSection title="Recherche / géographie">
          <FilterLabel>Recherche (nom, SIRET, email, téléphone)</FilterLabel>
          <Input
            className="mt-1.5"
            placeholder="Au moins 2 caractères"
            value={p.filters.search ?? ''}
            onChange={(e) => p.setFilter('search', e.target.value || null)}
          />
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <FilterLabel>Code postal</FilterLabel>
              <Input
                className="mt-1.5"
                placeholder="75008"
                value={p.filters.postal_code ?? ''}
                onChange={(e) => p.setFilter('postal_code', e.target.value || null)}
              />
            </div>
            <div>
              <FilterLabel>Département</FilterLabel>
              <Input
                className="mt-1.5"
                placeholder="75, 971…"
                value={p.filters.department ?? ''}
                onChange={(e) => p.setFilter('department', e.target.value || null)}
              />
            </div>
          </div>
          <FilterLabel className="mt-4">Rayon autour d&apos;un lieu</FilterLabel>
          <div className="mt-1.5 grid grid-cols-[1fr_110px] gap-2">
            <Input
              placeholder="Ville ou code postal"
              value={p.filters.near_location ?? ''}
              onChange={(e) => p.setFilter('near_location', e.target.value || null)}
            />
            <Input
              placeholder="Rayon (km)"
              type="number"
              step="1"
              min="1"
              value={p.filters.radius_km ?? ''}
              onChange={(e) => p.setFilter('radius_km', e.target.value || null)}
            />
          </div>
          <p className="mt-1 text-[11px] text-text-muted">
            Les 2 champs sont nécessaires. Le lieu est géocodé automatiquement.
          </p>
        </FilterSection>

        {/* ===== 4. Activité commerciale ===== */}
        <FilterSection title="Activité commerciale">
          <FilterLabel>Opportunités</FilterLabel>
          <Select
            className="mt-1.5"
            value={p.filters.has_opportunities ?? ''}
            onChange={(e) => p.setFilter('has_opportunities', e.target.value || null)}
          >
            <option value="">Tous</option>
            <option value="true">A au moins une opp</option>
            <option value="false">Aucune opp</option>
          </Select>
          <label className="mt-4 flex items-center gap-2 text-xs text-text-primary">
            <input
              type="checkbox"
              checked={p.filters.has_signed_opp === 'true'}
              onChange={(e) => p.setFilter('has_signed_opp', e.target.checked ? 'true' : null)}
              className="accent-accent"
            />
            A au moins une opportunité signée
          </label>
          <FilterLabel>Stage d&apos;une opp (multi-OR)</FilterLabel>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {ALL_STAGES.map((s) => {
              const active = activeStages.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleActiveStage(s)}
                  aria-pressed={active}
                  className={cn(
                    'rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                    active
                      ? 'border-accent bg-accent text-white'
                      : 'border-border-subtle bg-white text-text-muted hover:border-accent/40 hover:text-brand',
                  )}
                >
                  {STAGE_LABELS[s] ?? s}
                </button>
              );
            })}
          </div>
          <FilterLabel>Types de contrats souscrits (multi-OR)</FilterLabel>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {filteredContractTypes.map((ct) => {
              const active = subscribedTypes.includes(ct.id);
              return (
                <button
                  key={ct.id}
                  type="button"
                  onClick={() => toggleContractType(ct.id)}
                  aria-pressed={active}
                  className={cn(
                    'rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition-colors',
                    active
                      ? 'border-accent bg-accent text-white'
                      : 'border-border-subtle bg-white text-text-muted hover:border-accent/40 hover:text-brand',
                  )}
                  title={ct.label}
                >
                  {ct.code}
                </button>
              );
            })}
          </div>
          <FilterLabel>CA signé cumulé ≥ (€)</FilterLabel>
          <Input
            className="mt-1.5"
            type="number"
            min="0"
            step="100"
            placeholder="Ex. 1000"
            value={p.filters.min_signed_revenue ?? ''}
            onChange={(e) => p.setFilter('min_signed_revenue', e.target.value || null)}
          />
        </FilterSection>

        {/* ===== 5. Profil entreprise ===== */}
        {/* Masqué pour les particuliers : tous ces filtres sont B2B-only
            (effectif, CA entreprise, date création société, NAF, convention
            collective). Backend les ignore silencieusement pour type=individual,
            mais les cacher côté UI évite toute confusion. */}
        {prospectType !== 'individual' && (
          <FilterSection title="Profil entreprise">
            <FilterLabel>Effectif</FilterLabel>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <Input
                type="number"
                min="0"
                placeholder="Min"
                value={p.filters.min_employee_count ?? ''}
                onChange={(e) => p.setFilter('min_employee_count', e.target.value || null)}
              />
              <Input
                type="number"
                min="0"
                placeholder="Max"
                value={p.filters.max_employee_count ?? ''}
                onChange={(e) => p.setFilter('max_employee_count', e.target.value || null)}
              />
            </div>
            <FilterLabel>CA entreprise (€)</FilterLabel>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <Input
                type="number"
                min="0"
                placeholder="Min"
                value={p.filters.min_revenue ?? ''}
                onChange={(e) => p.setFilter('min_revenue', e.target.value || null)}
              />
              <Input
                type="number"
                min="0"
                placeholder="Max"
                value={p.filters.max_revenue ?? ''}
                onChange={(e) => p.setFilter('max_revenue', e.target.value || null)}
              />
            </div>
            <FilterLabel>Date de création société</FilterLabel>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              <Input
                type="date"
                value={isoToDate(p.filters.company_created_after)}
                onChange={(e) =>
                  p.setFilter('company_created_after', dateToIso(e.target.value) || null)
                }
              />
              <Input
                type="date"
                value={isoToDate(p.filters.company_created_before)}
                onChange={(e) =>
                  p.setFilter('company_created_before', dateToIso(e.target.value) || null)
                }
              />
            </div>
            <FilterLabel>Code NAF</FilterLabel>
            <Input
              className="mt-1.5"
              placeholder="6201Z"
              value={p.filters.naf_code ?? ''}
              onChange={(e) => p.setFilter('naf_code', e.target.value || null)}
            />
            <FilterLabel>Convention collective</FilterLabel>
            <Input
              className="mt-1.5"
              placeholder="Recherche partielle"
              value={p.filters.collective_agreement ?? ''}
              onChange={(e) => p.setFilter('collective_agreement', e.target.value || null)}
            />
          </FilterSection>
        )}

        {/* ===== 6. Temporalité CRM ===== */}
        <FilterSection title="Temporalité">
          <FilterLabel>Créé entre</FilterLabel>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={isoToDate(p.filters.created_after)}
              onChange={(e) => p.setFilter('created_after', dateToIso(e.target.value) || null)}
            />
            <Input
              type="date"
              value={isoToDate(p.filters.created_before)}
              onChange={(e) => p.setFilter('created_before', dateToIso(e.target.value) || null)}
            />
          </div>
          <FilterLabel>Modifié entre</FilterLabel>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={isoToDate(p.filters.updated_after)}
              onChange={(e) => p.setFilter('updated_after', dateToIso(e.target.value) || null)}
            />
            <Input
              type="date"
              value={isoToDate(p.filters.updated_before)}
              onChange={(e) => p.setFilter('updated_before', dateToIso(e.target.value) || null)}
            />
          </div>
          <FilterLabel>Dernière activité entre</FilterLabel>
          <div className="mt-1.5 grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={isoToDate(p.filters.last_activity_after)}
              onChange={(e) =>
                p.setFilter('last_activity_after', dateToIso(e.target.value) || null)
              }
            />
            <Input
              type="date"
              value={isoToDate(p.filters.last_activity_before)}
              onChange={(e) =>
                p.setFilter('last_activity_before', dateToIso(e.target.value) || null)
              }
            />
          </div>
          <FilterLabel>Sans activité depuis (jours)</FilterLabel>
          <Input
            className="mt-1.5"
            type="number"
            min="1"
            placeholder="Ex. 60"
            value={p.filters.no_activity_since ?? ''}
            onChange={(e) => p.setFilter('no_activity_since', e.target.value || null)}
          />
        </FilterSection>

        {/* ===== 7. Admin ===== */}
        {p.isDirection && (
          <FilterSection title="Admin">
            <label className="flex items-center gap-2 text-xs text-text-primary">
              <input
                type="checkbox"
                checked={p.filters.include_deleted === 'true'}
                onChange={(e) => p.setFilter('include_deleted', e.target.checked ? 'true' : null)}
                className="accent-accent"
              />
              Inclure la corbeille (prospects supprimés dans les 24 h)
            </label>
          </FilterSection>
        )}
      </div>
    </Drawer>
  );
}

// ===========================================================================
// Active filter pills - au-dessus du tableau, représente chaque filtre actif
// ===========================================================================

interface ActiveFilterPillsProps {
  filters: FilterValues;
  contractTypes: ContractType[];
  teamMembers: TeamMember[];
  onRemove: (key: FilterKey) => void;
  onClearAll: () => void;
}

function ActiveFilterPills({
  filters,
  contractTypes,
  teamMembers,
  onRemove,
  onClearAll,
}: ActiveFilterPillsProps) {
  const pills = useMemo(
    () => buildPills(filters, contractTypes, teamMembers),
    [filters, contractTypes, teamMembers],
  );
  if (pills.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      {pills.map((pill) => (
        <button
          key={pill.key}
          type="button"
          onClick={() => onRemove(pill.key)}
          className="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2.5 py-1 text-[11px] font-medium text-accent-600 transition-colors hover:bg-accent hover:text-white"
          aria-label={`Retirer le filtre ${pill.label}`}
        >
          <span>{pill.label}</span>
          <X className="h-3 w-3" />
        </button>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-[11px] font-medium text-text-muted underline-offset-2 hover:text-accent hover:underline"
      >
        Tout effacer
      </button>
    </div>
  );
}

// Construit la liste des pills à partir des filtres actifs. Lit les
// référentiels pour afficher le LABEL plutôt que l'UUID brut (ex.
// « Commercial : Pierre Dupont » au lieu de « assigned_user_id : abc-…. »).
function buildPills(
  filters: FilterValues,
  contractTypes: ContractType[],
  teamMembers: TeamMember[],
): Array<{ key: FilterKey; label: string }> {
  const pills: Array<{ key: FilterKey; label: string }> = [];
  const push = (key: FilterKey, label: string) => pills.push({ key, label });

  const labelByUserId = new Map(teamMembers.map((m) => [m.id, `${m.firstName} ${m.lastName}`]));
  const labelByContractId = new Map(contractTypes.map((c) => [c.id, c.code]));

  const STATUS_LABELS: Record<string, string> = { cold: 'Froid', warm: 'Chaud', client: 'Client' };
  const POT_LABELS: Record<string, string> = {
    low: 'Faible',
    medium: 'Moyen',
    high: 'Fort',
    very_high: 'Très fort',
  };

  const TYPE_LABELS: Record<string, string> = { company: 'Entreprise', individual: 'Particulier' };
  if (filters.type) push('type', `Type : ${TYPE_LABELS[filters.type] ?? filters.type}`);
  if (filters.status) push('status', `Statut : ${STATUS_LABELS[filters.status] ?? filters.status}`);
  if (filters.potential)
    push('potential', `Potentiel : ${POT_LABELS[filters.potential] ?? filters.potential}`);
  if (filters.assigned_user_id)
    push(
      'assigned_user_id',
      `Commercial : ${labelByUserId.get(filters.assigned_user_id) ?? filters.assigned_user_id}`,
    );
  if (filters.search) push('search', `Recherche : ${filters.search}`);
  if (filters.naf_code) push('naf_code', `NAF : ${filters.naf_code}`);
  if (filters.postal_code) push('postal_code', `CP : ${filters.postal_code}`);
  if (filters.department) push('department', `Dép. : ${filters.department}`);
  if (filters.near_location && filters.radius_km)
    // Pill combiné sur la clé `near_location` - retirer ce pill enlève
    // implicitement le rayon aussi (via l'effet `refine` côté backend, mais
    // on enchaîne aussi côté front). Géré via un `onRemove` custom côté
    // parent (ici on se contente d'exposer la clé principale).
    push('near_location', `Rayon : ${filters.radius_km} km autour de ${filters.near_location}`);
  if (filters.has_opportunities === 'true') push('has_opportunities', 'A des opps');
  if (filters.has_opportunities === 'false') push('has_opportunities', 'Aucune opp');
  if (filters.has_signed_opp === 'true') push('has_signed_opp', 'A signé');
  if (filters.active_opp_stages) {
    const stages = filters.active_opp_stages
      .split(',')
      .map((s) => STAGE_LABELS[s as OpportunityStage] ?? s)
      .join(', ');
    push('active_opp_stages', `Stage opp : ${stages}`);
  }
  if (filters.subscribed_contract_type_ids) {
    const codes = filters.subscribed_contract_type_ids
      .split(',')
      .map((id) => labelByContractId.get(id) ?? id.slice(0, 4))
      .join(', ');
    push('subscribed_contract_type_ids', `Contrats : ${codes}`);
  }
  if (filters.min_signed_revenue)
    push('min_signed_revenue', `CA signé ≥ ${filters.min_signed_revenue} €`);
  if (filters.min_employee_count || filters.max_employee_count) {
    const range = `${filters.min_employee_count ?? '0'}-${filters.max_employee_count ?? '∞'}`;
    // Un seul pill pour le range (les deux bornes vont de pair UX-wise) -
    // on associe la clé la plus basse qui, au retrait, enlèvera aussi l'autre
    // via `onRemove` dédié. Pour éviter d'avoir à multiplier la logique, on
    // retire juste celle cliquée ; l'utilisateur peut retirer l'autre aussi.
    if (filters.min_employee_count)
      push('min_employee_count', `Effectif min : ${filters.min_employee_count}`);
    if (filters.max_employee_count)
      push('max_employee_count', `Effectif max : ${filters.max_employee_count}`);
    void range;
  }
  if (filters.min_revenue) push('min_revenue', `CA min : ${filters.min_revenue} €`);
  if (filters.max_revenue) push('max_revenue', `CA max : ${filters.max_revenue} €`);
  if (filters.company_created_after)
    push('company_created_after', `Société depuis : ${filters.company_created_after.slice(0, 10)}`);
  if (filters.company_created_before)
    push(
      'company_created_before',
      `Société jusqu'à : ${filters.company_created_before.slice(0, 10)}`,
    );
  if (filters.collective_agreement)
    push('collective_agreement', `Conv. coll. : ${filters.collective_agreement}`);
  if (filters.created_after)
    push('created_after', `Créé après : ${filters.created_after.slice(0, 10)}`);
  if (filters.created_before)
    push('created_before', `Créé avant : ${filters.created_before.slice(0, 10)}`);
  if (filters.updated_after)
    push('updated_after', `Modifié après : ${filters.updated_after.slice(0, 10)}`);
  if (filters.updated_before)
    push('updated_before', `Modifié avant : ${filters.updated_before.slice(0, 10)}`);
  if (filters.last_activity_after)
    push('last_activity_after', `Activité après : ${filters.last_activity_after.slice(0, 10)}`);
  if (filters.last_activity_before)
    push('last_activity_before', `Activité avant : ${filters.last_activity_before.slice(0, 10)}`);
  if (filters.no_activity_since)
    push('no_activity_since', `Sans activité : ${filters.no_activity_since} j`);
  if (filters.include_deleted === 'true') push('include_deleted', 'Corbeille incluse');
  return pills;
}

// ===========================================================================
// SearchBar - barre de recherche au-dessus du tableau (style Stripe Dashboard)
// ===========================================================================

function SearchBar({
  value,
  onChange,
  onClear,
}: {
  value: string;
  onChange: (v: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="relative w-full max-w-[480px]">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Recherche (nom, SIRET, email, téléphone…)"
        aria-label="Rechercher un prospect"
        className={cn(
          'h-9 w-full rounded-md border border-border-subtle bg-white pl-9 pr-9 text-sm text-brand placeholder:text-text-muted',
          'shadow-[inset_0_1px_2px_rgba(27,42,71,0.04)]',
          // Pas de changement de couleur au focus (demande UX) : juste retirer
          // l'outline natif. La bordure reste `border-border-subtle`.
          'focus:outline-none',
        )}
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Effacer la recherche"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-text-muted transition-colors hover:bg-gray-100 hover:text-brand"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ===========================================================================
// Helpers - Section / Label UI (mêmes patterns que la FiltersDrawer Pipeline)
// ===========================================================================

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  // Section drawer style Stripe Dashboard 2024 : aéré (py-7), titre fin en
  // majuscules tracking-wider, séparateurs subtils. Le spacing entre groupes
  // label+input est géré par `mt-4 first:mt-0` posé sur les FilterLabel, et
  // l'espace label→input par `mt-1.5` sur les inputs (pattern existant).
  // Pas de `space-y-*` ici pour ne pas ajouter d'espace entre label et input.
  return (
    <section className="border-t border-border-subtle py-7 first:border-t-0 first:pt-0 last:pb-0">
      <h3 className="mb-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-muted">
        {title}
      </h3>
      {children}
    </section>
  );
}

function FilterLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  // Label de champ Stripe : sentence case, 12 px, couleur sobre. `mt-4` crée
  // l'espace vertical entre groupes (label+input suivant), `first:mt-0`
  // annule pour le premier label de chaque conteneur (y compris dans un
  // grid où chaque label est seul dans son div parent).
  return (
    <label className={cn('mt-4 block text-[12px] font-medium text-brand first:mt-0', className)}>
      {children}
    </label>
  );
}
